/**
 * Punkt 121 (Antoine, 25.09.2026): „bei jeder Anfrage sollte stehen, über welches Formular sie
 * gekommen ist“. Die Pille zeigt `herkunft.text` (Eingang, sonst Kanal) — die Regel steht EINMAL
 * in `herkunftAbleiten` (packages/auswertung), der Server liefert das Ergebnis mit. Hier wird
 * nichts abgeleitet, nur angezeigt. Testanfragen tragen zusätzlich die Pille „Test“.
 * Widget-Anfragen zeigen ihre Anfragenummer („A-3F9C21“) — dieselbe, die der Kunde in der
 * Bestätigungsmail hat (nur die Widget-Route verschickt sie).
 */
import type { Herkunft } from "@maestro/auswertung";
import { Icon } from "./components";
import { inquirySourceMeta } from "./format";
import { anfrageNummer } from "../lib/anfrage-angaben";

const PILLE =
  "inline-flex max-w-full items-center gap-1 truncate rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/[0.08]";

export function HerkunftPille({
  herkunft,
  isTest,
  anfrage,
}: {
  herkunft?: Herkunft | null;
  isTest?: boolean;
  anfrage?: { id: string; widgetId?: string | null };
}) {
  // Nur http(s) wird zum Link (Server filtert ebenfalls; hier zur Sicherheit noch einmal).
  const seiteUrl = herkunft?.seiteUrl && /^https?:\/\//i.test(herkunft.seiteUrl) ? herkunft.seiteUrl : null;
  const nummer = anfrage?.widgetId ? anfrageNummer(anfrage.id) : null;
  if (!herkunft && !isTest && !nummer) return null;
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1">
      {herkunft && (seiteUrl ? (
        <a
          data-testid="herkunft-pille"
          href={seiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${PILLE} hover:underline`}
          title={`Kanal: ${herkunft.kanalTitel} · Seite öffnen`}
        >
          <Icon name={inquirySourceMeta(herkunft.kanal).icon} className="shrink-0 text-[12px]" />
          <span className="truncate">{herkunft.text}</span>
          <Icon name="open_in_new" className="shrink-0 text-[12px]" />
        </a>
      ) : (
        <span data-testid="herkunft-pille" className={PILLE} title={`Kanal: ${herkunft.kanalTitel}`}>
          <Icon name={inquirySourceMeta(herkunft.kanal).icon} className="shrink-0 text-[12px]" />
          <span className="truncate">{herkunft.text}</span>
        </span>
      ))}
      {nummer && (
        <span data-testid="anfrage-nummer-pille" className={`${PILLE} tabular-nums`} title="Anfragenummer aus der Bestätigungsmail">
          {nummer}
        </span>
      )}
      {isTest && (
        <span data-testid="test-pille" className={PILLE}>
          Test
        </span>
      )}
    </span>
  );
}
